import {
  type PatientMessagePreviewRequest,
  PatientMessagePreviewRequestSchema,
  type PatientMessagePreviewResponse,
  PatientMessagePreviewResponseSchema,
  type PatientMessageTestResponse,
  PatientMessageTestResponseSchema,
  type PatientMessagingSettingsResponse,
  PatientMessagingSettingsResponseSchema,
  PublishPatientMessageTemplateRequestSchema,
  type PublishPatientMessageTemplateResponse,
  PublishPatientMessageTemplateResponseSchema,
  RequestPatientMessageTestSchema,
  type SavePatientMessagingSettingsRequest,
  SavePatientMessagingSettingsRequestSchema,
  type SavePatientMessagingSettingsResponse,
  SavePatientMessagingSettingsResponseSchema,
} from "@emr/contracts";
import { z } from "zod";
import { SchedulingRequestError, schedulingRequestError } from "../demo/schedulingErrors";

/** What the template editor sends; the contract normalizes line breaks and trims first. */
export type PublishPatientMessageTemplateInput = z.input<
  typeof PublishPatientMessageTemplateRequestSchema
>;
/** A test message request as the editor builds it (a draft is normalized like a publish). */
export type RequestPatientMessageTestInput = z.input<typeof RequestPatientMessageTestSchema>;

/**
 * A clinic's confirmations and reminders (PA-8 / EXECDEV-215) on the staff scheduling lane:
 * its messaging settings, the wording of each kind of text and email, previews and test
 * messages. Every route acts on the client's own site (its `x-site-id`). Requests are checked
 * against the contract before they leave and responses after they arrive; errors are
 * SchedulingRequestError with a `detail` (`messaging_settings_changed`, `message_template_changed`,
 * `messaging_sender_missing`, `message_test_limit_reached`, and `messaging_not_offered` for a
 * test while no messaging service delivers).
 *
 * - GET  /v1/scheduling/messaging                 settings, senders, readiness, every template
 *                                                 (any staff role)
 * - POST /v1/scheduling/messaging/settings        save the settings (clinic_admin)
 * - POST /v1/scheduling/messaging/templates       publish a template's next version (clinic_admin)
 * - POST /v1/scheduling/messaging/preview         render a draft with sample details (any role)
 * - POST /v1/scheduling/messaging/tests           send a test message (clinic_admin)
 * - GET  /v1/scheduling/messaging/tests/:testId   the test's state (its requester)
 *
 * A preview and a test use sample details only, never a patient's. No response carries a full
 * phone number or address.
 */
export function createPatientMessagingSettingsClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    fallbackMessage: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(path, body, signal);
    if (!response.ok) throw await schedulingRequestError(response, fallbackMessage);
    return schema.parse(await response.json());
  }

  return {
    async getPatientMessagingSettings(
      signal?: AbortSignal,
    ): Promise<PatientMessagingSettingsResponse> {
      return json(
        "/v1/scheduling/messaging",
        PatientMessagingSettingsResponseSchema,
        "The clinic's message settings could not be loaded.",
        undefined,
        signal,
      );
    },

    /**
     * Saves the whole settings against `expectedVersion`. Identical settings save nothing
     * (`saved: false`); a save cancels or plans the clinic's queued messages (`impact`).
     */
    async savePatientMessagingSettings(
      input: SavePatientMessagingSettingsRequest,
      signal?: AbortSignal,
    ): Promise<SavePatientMessagingSettingsResponse> {
      return json(
        "/v1/scheduling/messaging/settings",
        SavePatientMessagingSettingsResponseSchema,
        "The message settings could not be saved.",
        SavePatientMessagingSettingsRequestSchema.parse(input),
        signal,
      );
    },

    /**
     * Publishes the next version of a kind and channel's wording against `expectedVersion`. Text
     * identical to the current version publishes nothing (`published: false`).
     */
    async publishPatientMessageTemplate(
      input: PublishPatientMessageTemplateInput,
      signal?: AbortSignal,
    ): Promise<PublishPatientMessageTemplateResponse> {
      return json(
        "/v1/scheduling/messaging/templates",
        PublishPatientMessageTemplateResponseSchema,
        "The new wording could not be published.",
        PublishPatientMessageTemplateRequestSchema.parse(input),
        signal,
      );
    },

    /** The draft rendered by the server with sample details; problems come back as issues. */
    async previewPatientMessage(
      input: PatientMessagePreviewRequest,
      signal?: AbortSignal,
    ): Promise<PatientMessagePreviewResponse> {
      return json(
        "/v1/scheduling/messaging/preview",
        PatientMessagePreviewResponseSchema,
        "The preview could not be loaded.",
        PatientMessagePreviewRequestSchema.parse(input),
        signal,
      );
    },

    /** Queues a test message with sample details: a text to the typed number, an email to you. */
    async requestPatientMessageTest(
      input: RequestPatientMessageTestInput,
      signal?: AbortSignal,
    ): Promise<PatientMessageTestResponse> {
      return json(
        "/v1/scheduling/messaging/tests",
        PatientMessageTestResponseSchema,
        "The test message could not be sent.",
        RequestPatientMessageTestSchema.parse(input),
        signal,
      );
    },

    async getPatientMessageTest(
      testId: string,
      signal?: AbortSignal,
    ): Promise<PatientMessageTestResponse> {
      return json(
        `/v1/scheduling/messaging/tests/${encodeURIComponent(z.string().uuid().parse(testId))}`,
        PatientMessageTestResponseSchema,
        "The test message's state could not be loaded.",
        undefined,
        signal,
      );
    },
  };
}

/** The settings read in the query cache: the admin page and the admin overview's state. */
export const patientMessagingSettingsKey = (siteId: string) =>
  ["patient-messaging-settings", siteId] as const;

export type PatientMessagingSettingsClient = ReturnType<
  typeof createPatientMessagingSettingsClient
>;

/** The route is not there: this clinic's schedule lane (or this agent-api) has no settings yet. */
export function isMessagingSettingsUnavailable(error: unknown): boolean {
  return error instanceof SchedulingRequestError && error.status === 404 && !error.detail;
}

/** The signed-in role may not read or change this (401/403). */
export function isMessagingSettingsRefusal(error: unknown): boolean {
  return error instanceof SchedulingRequestError && (error.status === 401 || error.status === 403);
}
